/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React, { useCallback, useContext, useMemo } from 'react';
import {
  CreateLivefireCardCallbacks,
  LivefireProps,
  OrganismState,
  StatusActions,
  ViewState,
} from '../types';
import { useMetricFilterOpen } from '../status';
import { metric, metricSearch } from '../util';
import { defaultMenuConfig } from '../menuItems';
import { createLivefireCard } from '../ui';
import { onScrollHandler } from '../scroll';
import { WrappedLoadingDots } from './WrappedLoadingDots';
import { FilterList } from './FilterList';
import { NonUndefined } from '@/functions/typeHelpers';
import { MetroContext } from '@/classes';
import { useId } from '@/functions';
import { ErrorScreen, ManagedSearchBar } from '@/molecules';
import { Button } from '@/atoms';
import { Filter } from '@/svgs';

export type FeedProps = {
  organismState: OrganismState;
  callbacks: CreateLivefireCardCallbacks;
  menuConfig: NonUndefined<LivefireProps['menuConfig']>;
  dispatch: React.Dispatch<StatusActions>;
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  isRebrand?: boolean;
};

export function Feed({
  organismState,
  callbacks,
  menuConfig,
  dispatch,
  viewState,
  setViewState,
  isRebrand = false,
}: FeedProps) {
  useMetricFilterOpen(viewState);
  const [filterButtonId, filterListId] = useId(2, 'livefire-feed-');
  const metroContext = useContext(MetroContext);

  // trigger the search and a delayed search metric
  const filter = useCallback(
    (searchTerm) => {
      setViewState((state) => ({ ...state, searchTerm }));
      if (searchTerm.trim() !== '') {
        metricSearch(metroContext);
      }
    },
    [metroContext, setViewState],
  );

  const {
    filtered,
    filteredItems,
    filterButtonActive,
    itemTypeFilterEmptiedList,
    showFilterOptions,
    showHeader,
  } = viewState;

  const { cardMenus: defaultMenus } = defaultMenuConfig;
  const { cardMenus } = menuConfig;

  const menus = useMemo(
    () => ({
      ...defaultMenus,
      ...cardMenus,
    }),
    [cardMenus, defaultMenus],
  );

  if (!filtered) {
    return <WrappedLoadingDots />;
  }

  let contents = (
    <div className="error-container">
      <ErrorScreen
        body="We couldn’t find any tracking for "
        icon="cancelCircle"
        iconClassName="no-results"
        title="No Results Found"
      >
        <p className="body strong">
          &ldquo;
          {viewState.searchTerm}
          &rdquo;
        </p>
      </ErrorScreen>
    </div>
  );

  if (filteredItems.length > 0) {
    const mapCard = createLivefireCard.bind(
      null,
      metroContext,
      callbacks,
      menus,
      menuConfig,
      dispatch,
    );
    const cards = filteredItems.map(mapCard);

    contents = (
      <div className="feed-container" aria-live="polite">
        {cards}
      </div>
    );
  } else if (itemTypeFilterEmptiedList) {
    contents = (
      <div className="error-container too-filtered">
        <ErrorScreen
          body="We couldn't find what you are looking for. Try a different filter option."
          icon="filter"
          title="No Results Found"
        />
      </div>
    );
  }

  const feedClass = [
    'feed',
    `position--${organismState.scrollPosition}`,
    `first-tooltip--${organismState.firstTooltipVisible}`,
  ];

  return (
    <div className="livefire-contents">
      {showHeader && (
        <div className="livefire-header">
          <ManagedSearchBar
            className="searchbar"
            size="medium"
            onChange={filter}
            onSelect={filter}
            onFocus={metric('livefire.searchbar.focus', metroContext)}
            value={viewState.searchTerm}
          />
          {showFilterOptions && (
            <Button
              className={`filters ${filterButtonActive ? 'active' : ''}`}
              icon={<Filter size={`${9 / 2.85}`} title="Filter" />}
              iconPosition="after"
              label="Filter"
              id={filterButtonId}
              aria-controls={filterListId}
              onClick={() => {
                setViewState((state) => ({
                  ...state,
                  filterButtonActive: !filterButtonActive,
                }));
              }}
            />
          )}
        </div>
      )}
      <div
        className={feedClass.join(' ')}
        onScroll={onScrollHandler(metroContext, dispatch, metric)}
      >
        {contents}
      </div>
      {showFilterOptions && (
        <FilterList
          id={filterListId}
          aria-labelledby={filterButtonId}
          viewState={viewState}
          setViewState={setViewState}
        />
      )}
    </div>
  );
}
